import * as React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const EXIT_DELAY = 0.15;

interface DropdownMenuContextType {
  isOpen: boolean;
  activeValue: string | null;
  activeSubmenu: string | null;
  setActiveValue: (value: string | null) => void;
  setActiveSubmenu: (value: string | null) => void;
  scheduleReset: () => void;
  clearReset: () => void;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextType>({
  isOpen: false,
  activeValue: null,
  activeSubmenu: null,
  setActiveValue: () => {},
  setActiveSubmenu: () => {},
  scheduleReset: () => {},
  clearReset: () => {},
});

const useDropdownMenu = (): DropdownMenuContextType => {
  const context = React.useContext(DropdownMenuContext);
  if (!context) {
    throw new Error('useDropdownMenu must be used within a DropdownMenu');
  }
  return context;
};

type DropdownMenuProps = React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root>;
const DropdownMenu: React.FC<DropdownMenuProps> = ({ children, ...props }) => {
  const [isOpen, setIsOpen] = React.useState(props?.open ?? props?.defaultOpen ?? false);
  const [activeValue, setActiveValueState] = React.useState<string | null>(null);
  const [activeSubmenu, setActiveSubmenuState] = React.useState<string | null>(null);

  const exitTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleReset = React.useCallback(() => {
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
    }
    exitTimeoutRef.current = setTimeout(() => {
      setActiveValueState(null);
      setActiveSubmenuState(null);
      exitTimeoutRef.current = null;
    }, EXIT_DELAY * 1000);
  }, []);

  const clearReset = React.useCallback(() => {
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);

  const setActiveValue = (val: string | null) => {
    clearReset();
    setActiveValueState(val);
  };

  const setActiveSubmenu = (val: string | null) => {
    clearReset();
    setActiveSubmenuState(val);
  };

  const handleOpenChange = React.useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setActiveValue(null);
      setActiveSubmenu(null);
    }
    props.onOpenChange?.(open);
  }, [props]);

  return (
    <DropdownMenuPrimitive.Root {...props} onOpenChange={handleOpenChange}>
      <DropdownMenuContext.Provider value={{
        isOpen,
        activeValue,
        activeSubmenu,
        setActiveValue,
        setActiveSubmenu,
        scheduleReset,
        clearReset,
      }}>
        {children}
      </DropdownMenuContext.Provider>
    </DropdownMenuPrimitive.Root>
  );
};

const DropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger ref={ref} className={className} {...props} />
));
DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, children, sideOffset = 16, ...props }, ref) => {
  const { isOpen } = useDropdownMenu();

  return (
    <AnimatePresence>
      {isOpen && (
        <DropdownMenuPrimitive.Portal forceMount>
          <DropdownMenuPrimitive.Content ref={ref} sideOffset={sideOffset} asChild {...props} className="select-none">
            <motion.div
              key="dropdown-menu"
              className={cn(
                'z-50 min-w-[8rem] overflow-visible rounded-lg border border-accent-blue/20 bg-deep-blue shadow-lg',
                className
              )}
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 40,
                mass: 1.2
              }}
              style={{ willChange: 'transform, opacity' }}
            >
              {children}
            </motion.div>
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      )}
    </AnimatePresence>
  );
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

interface DropdownMenuSubContentProps {
  children: React.ReactNode;
  className?: string;
  submenuId: string;
}

const DropdownMenuSubContent = React.forwardRef<
  HTMLDivElement,
  DropdownMenuSubContentProps
>(({ children, className, submenuId, ...props }, ref) => {
  const { activeSubmenu } = useDropdownMenu();
  const isActive = activeSubmenu === submenuId;

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          ref={ref}
          className={cn(
            'w-full border-t border-accent-blue/20 bg-deep-blue',
            className
          )}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 40,
            mass: 1
          }}
          {...props}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
});
DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';

interface DropdownMenuItemProps extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  inset?: boolean;
  submenu?: React.ReactNode;
  submenuId?: string;
}

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownMenuItemProps
>(({ className, children, inset, disabled, submenu, submenuId, onClick, ...props }, ref) => {
  const { activeValue, setActiveValue, activeSubmenu, setActiveSubmenu } = useDropdownMenu();
  const id = React.useId();

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (submenuId) {
      e.preventDefault();
      if (activeSubmenu === submenuId) {
        setActiveSubmenu(null);
        setActiveValue(null);
      } else {
        setActiveSubmenu(submenuId);
        setActiveValue(id);
      }
    }
    onClick?.(e);
  };

  return (
    <>
      <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(
          "relative h-[38px] outline-none",
          submenu && "submenu-trigger",
          "focus:bg-accent-blue/10 focus:text-electric-blue select-none",
          "data-[highlighted]:bg-accent-blue/10 data-[highlighted]:text-electric-blue select-none"
        )}
        {...props}
        disabled={disabled}
        onClick={handleClick}
      >
        <AnimatePresence>
          {activeValue === id && !disabled && (
            <motion.span
              className="absolute inset-0 w-full bg-accent-blue/10"
              layoutId="dropdown-menu-item-background"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                mass: 1
              }}
            />
          )}
        </AnimatePresence>

        <motion.div
          data-disabled={disabled}
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
          transition={{ 
            type: "spring", 
            stiffness: 300, 
            damping: 30,
            mass: 1
          }}
          className={cn(
            'relative z-[1] flex h-full w-full items-center justify-between px-3 text-sm outline-none transition-colors',
            'hover:text-electric-blue',
            'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            inset && 'pl-8',
            className
          )}
        >
          <div className="flex items-center gap-2">
            {children}
          </div>
          {submenu && (
            <ChevronDown
              className={cn(
                "h-4 w-4 text-text-light/60 transition-transform duration-200",
                activeSubmenu === submenuId && "transform rotate-180"
              )}
            />
          )}
        </motion.div>
      </DropdownMenuPrimitive.Item>
      {submenu && (
        <DropdownMenuSubContent submenuId={submenuId!}>
          {submenu}
        </DropdownMenuSubContent>
      )}
    </>
  );
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('mx-2 my-1 h-px bg-accent-blue/20', className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
};